import {useQuery} from '@github-ui/react-query'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CopilotLicenseType, type CopilotChatPayload} from '@github-ui/copilot-chat/utils/copilot-chat-types'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {CopilotChatEntitlementResult} from '@github-ui/copilot-chat/utils/copilot-chat-entitlement'

/**
 * Hook to get the Copilot license type.
 *
 * This hook tries to get the license type from the app payload first (available in React routes).
 * If the payload is not available (e.g., in Memex or other non-React-route contexts),
 * it fetches the entitlement from the API.
 */
export function useCopilotLicenseType(): CopilotLicenseType {
  // Try to get license type from app payload first
  const payload = useAppPayload<CopilotChatPayload>()
  const payloadLicenseType = payload?.licenseType

  // Fetch from API only if payload is not available
  const {data} = useQuery<CopilotChatEntitlementResult>({
    queryKey: ['copilot-chat', 'entitlement'],
    queryFn: async () => {
      const response = await verifiedFetchJSON('/github-copilot/chat/entitlement')

      if (!response.ok) {
        throw new Error(`Failed to retrieve Copilot chat entitlement (${response.status} on ${response.url})`)
      }

      return (await response.json()) as CopilotChatEntitlementResult
    },
    // Only fetch if payload license type is not available
    enabled: payloadLicenseType === undefined,
    placeholderData: {
      licenseType: CopilotLicenseType.Unlicensed,
    },
    // like in packages/copilot-chat/utils/copilot-chat-entitlement.ts
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  return payloadLicenseType ?? data?.licenseType ?? CopilotLicenseType.Unlicensed
}
